import { ACTION } from './actions'
import { createEmptySchedule } from '../models/createEmptySchedule'
import { createEmptyAnchor } from '../models/createEmptyAnchor'
import { createEmptyTargetingRow } from '../models/createEmptyTargetingRow'
import { createEmptyEndRule } from '../models/createEmptyEndRules'
import { RESOLVE_TO } from '../constants'
import { clearAnchorErrorsForUpdate } from '../validation/clearAnchorErrors'

const replaceSchedule = (state, scheduleId, updater) => ({
  ...state,
  schedules: state.schedules.map(schedule => (schedule.id === scheduleId ? updater(schedule) : schedule)),
})

export const initialScheduleState = {
  schedules: [createEmptySchedule()],
  activeScheduleId: null,
  errors: {},
  saving: false,
}

export const scheduleReducer = (state, action) => {
  switch (action.type) {
    case ACTION.hydrate: {
      const schedules = action.schedules || []
      const preservedActive =
        state.activeScheduleId && schedules.some(s => s.id === state.activeScheduleId)
          ? state.activeScheduleId
          : null
      return {
        ...state,
        schedules,
        activeScheduleId:
          action.activeScheduleId !== undefined
            ? action.activeScheduleId
            : preservedActive ?? schedules[0]?.id ?? null,
        errors: {},
      }
    }
    case ACTION.selectSchedule:
      return { ...state, activeScheduleId: action.scheduleId }
    case ACTION.setErrors:
      return { ...state, errors: action.errors || {} }
    case ACTION.setSaving:
      return { ...state, saving: !!action.saving }
    case ACTION.updateSchedule:
      return replaceSchedule(state, action.scheduleId, schedule => ({ ...schedule, ...action.patch }))
    case ACTION.addSchedule: {
      const next = createEmptySchedule()
      return { ...state, schedules: [...state.schedules, next], activeScheduleId: next.id }
    }
    case ACTION.deleteSchedule: {
      if (state.schedules.length <= 1) return state
      const schedules = state.schedules.filter(schedule => schedule.id !== action.scheduleId)
      const activeScheduleId =
        state.activeScheduleId === action.scheduleId ? schedules[0].id : state.activeScheduleId
      return { ...state, schedules, activeScheduleId }
    }
    case ACTION.updateAnchor: {
      const scheduleIndex = state.schedules.findIndex(item => item.id === action.scheduleId)
      const schedule = state.schedules[scheduleIndex]
      const anchorIndex = schedule?.anchors.findIndex(item => item.id === action.anchorId) ?? -1
      const previousType = schedule?.anchors[anchorIndex]?.type
      const errors = clearAnchorErrorsForUpdate(
        state.errors,
        scheduleIndex,
        anchorIndex,
        action.patch,
        previousType,
      )
      return {
        ...replaceSchedule(state, action.scheduleId, current =>
          ({
            ...current,
            anchors: current.anchors.map(anchor =>
              anchor.id === action.anchorId ? { ...anchor, ...action.patch } : anchor,
            ),
          }),
        ),
        errors,
      }
    }
    case ACTION.addAnchor:
      return replaceSchedule(state, action.scheduleId, schedule => {
        const anchors = [...schedule.anchors, createEmptyAnchor()]
        return {
          ...schedule,
          anchors,
          combineAnchors: anchors.length > 1,
          resolveTo: schedule.resolveTo || RESOLVE_TO.latestOf,
        }
      })
    case ACTION.removeAnchor:
      return replaceSchedule(state, action.scheduleId, schedule => {
        if (schedule.anchors.length <= 1) return schedule
        const anchors = schedule.anchors.filter(anchor => anchor.id !== action.anchorId)
        if (anchors.length === schedule.anchors.length) return schedule
        return {
          ...schedule,
          anchors,
          combineAnchors: anchors.length > 1,
        }
      })
    case ACTION.setCombine:
      return replaceSchedule(state, action.scheduleId, schedule => {
        if (action.enabled) {
          const anchors =
            schedule.anchors.length >= 2 ? schedule.anchors : [...schedule.anchors, createEmptyAnchor()]
          return {
            ...schedule,
            combineAnchors: true,
            resolveTo: schedule.resolveTo || RESOLVE_TO.latestOf,
            anchors,
          }
        }
        return { ...schedule, combineAnchors: false, anchors: [schedule.anchors[0]] }
      })
    case ACTION.addTargeting:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        targeting: [...schedule.targeting, createEmptyTargetingRow()],
      }))
    case ACTION.updateTargeting:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        targeting: schedule.targeting.map(row => (row.id === action.rowId ? { ...row, ...action.patch } : row)),
      }))
    case ACTION.removeTargeting:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        targeting: schedule.targeting.filter(row => row.id !== action.rowId),
      }))
    case ACTION.addEndRule:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        endRules: {
          ...schedule.endRules,
          rules: [...(schedule.endRules?.rules || []), createEmptyEndRule()],
        },
      }))
    case ACTION.updateEndRule:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        endRules: {
          ...schedule.endRules,
          rules: (schedule.endRules?.rules || []).map(rule =>
            rule.id === action.ruleId ? { ...rule, ...action.patch } : rule,
          ),
        },
      }))
    case ACTION.removeEndRule:
      return replaceSchedule(state, action.scheduleId, schedule => ({
        ...schedule,
        endRules: {
          ...schedule.endRules,
          rules: (schedule.endRules?.rules || []).filter(rule => rule.id !== action.ruleId),
        },
      }))
    default:
      return state
  }
}
